import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  ArrowRight, 
  Leaf,
  Sun,
  Droplets,
  Search,
  Filter
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { generateCurrentWeekPosts, BlogPost } from '@/services/blogGenerator';

export function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Generate current week's posts
    const currentPosts = generateCurrentWeekPosts();
    setPosts(currentPosts);
    setFilteredPosts(currentPosts);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    let filtered = posts;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(query) ||
        post.excerpt.toLowerCase().includes(query) ||
        post.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    setFilteredPosts(filtered);
  }, [selectedCategory, searchQuery, posts]);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Ingredients': return <Leaf className="w-4 h-4" />;
      case 'Skin Concerns': return <Droplets className="w-4 h-4" />;
      case 'Routines': return <Sun className="w-4 h-4" />;
      default: return null;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Ingredients': return 'bg-green-100 text-green-800 border-green-200';
      case 'Skin Concerns': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Routines': return 'bg-amber-100 text-amber-800 border-amber-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Terracotta accent for category labels + AI-assisted pill, per design system
  const TERRACOTTA = '#E07A5F';

  const categories = ['all', 'Ingredients', 'Skin Concerns', 'Routines'];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#567D38] to-[#1A2E12] py-16 lg:py-24">
        <div className="container-marple">
          <div className="max-w-3xl mx-auto text-center">
            <div className="font-display uppercase tracking-[.12em] text-xs text-[#BDD630] mb-2">
              The Journal
            </div>
            <h1 className="heading-display text-white mb-6">
              Skin science, simply told
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              Expert insights on skincare, heritage botanicals, and the science
              behind radiant, healthy skin.
            </p>
          </div>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="sticky top-20 z-30 bg-white border-b border-gray-100 py-4">
        <div className="container-marple">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#1A2E12] focus:ring-2 focus:ring-[#1A2E12]/10"
              />
            </div>

            {/* Category Filters */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide">
              <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={cn(
                    "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
                    selectedCategory === category
                      ? "bg-[#1A2E12] text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  )}
                >
                  {category === 'all' ? 'All Posts' : category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="section">
        <div className="container-marple">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#1A2E12] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500">No posts found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {filteredPosts.map((post) => {
                const isAiAssisted = post.tags.some((tag) => tag.toLowerCase() === 'ai');
                return (
                  <Link
                    to={`/blog/${post.slug}`}
                    key={post.id}
                    className="group bg-white rounded-2xl border border-[#D9B38C]/40 overflow-hidden shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col"
                  >
                    {/* Image slot */}
                    <div className="relative h-[180px] overflow-hidden bg-gradient-to-br from-[#F7F5EE] to-[#EDE8DA]">
                      <div className="absolute inset-0 flex items-center justify-center text-[#567D38]/30">
                        {getCategoryIcon(post.category)}
                      </div>
                      {post.heroIngredient && (
                        <div className="absolute top-4 right-4">
                          <span className="px-3 py-1.5 bg-[#F4C95D] text-[#1A2E12] rounded-full text-xs font-medium">
                            {post.heroIngredient.replace('_', ' ')}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6 flex flex-col flex-1">
                      {/* Category + read time + AI badge row */}
                      <div className="flex items-center gap-2.5 mb-2.5 flex-wrap">
                        <span
                          className="font-display uppercase tracking-[.06em] text-[11px] font-semibold"
                          style={{ color: TERRACOTTA }}
                        >
                          {post.category}
                        </span>
                        <span className="text-gray-500 text-xs">
                          · {post.readingTime} min read
                        </span>
                        {isAiAssisted && (
                          <span className="inline-flex items-center gap-1 bg-[#BDD630]/15 text-[#567D38] text-[11px] px-2 py-[3px] rounded-full">
                            ✨ AI-assisted
                          </span>
                        )}
                      </div>

                      <h3 className="font-serif text-2xl text-[#1A2E12] mb-2 group-hover:text-[#567D38] transition-colors">
                        {post.title}
                      </h3>

                      <p className="text-[#6B7B6E] leading-relaxed text-sm mb-4">
                        {post.excerpt}
                      </p>

                      {/* Meta + tags */}
                      <div className="mt-auto">
                        <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(post.publishDate).toLocaleDateString('en-ZA', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {post.readingTime} min read
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-4">
                          {post.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>

                        <span className="inline-flex items-center gap-2 text-[#1A2E12] font-semibold text-sm group-hover:text-[#567D38] transition-colors">
                          Read Article
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="section bg-gray-50">
        <div className="container-marple">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-2 text-[#1A2E12] mb-4">
              Stay Updated
            </h2>
            <p className="text-gray-600 mb-8">
              Get weekly skincare insights, new product announcements, and 
              exclusive offers delivered to your inbox.
            </p>
            <div className="flex gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-[#1A2E12] focus:ring-2 focus:ring-[#1A2E12]/10"
              />
              <button className="btn btn-primary whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Blog;
